import { Component, Element, Fragment, h, Host, Prop, Watch } from "@stencil/core";

import axios from "axios";

import { StzhLocaleAdapter } from "../../../../index";

import { VBZ, VbzTickerDisturbance, VbzTickerInfoUrl, VbzTickerLine } from "../../../../index";

import { StzhVbzTickerLocalizedText } from "./stzh-vbz-ticker.localization";

/**
 * @slot link - Optional link to traffic situation
 */
@Component({
  tag: "stzh-vbz-ticker",
  styleUrl: "stzh-vbz-ticker.scss",
  scoped: true,
})
export class StzhVbzTicker {
  /** API URL (`{lang}` is replaced in the URL)  */
  @Prop() api: string =
    "https://fpbe.zvv.ch/restproxy/himsearch?format=json&accessId=unic&channels=ticker&additionalfields=tickers:VBZ&orderBy=LMOD_DESC&lang={lang}";

  /** Data to pass in directly (alternative for API call) */
  @Prop() data: any = "";
  private _data: any;

  /** More info link (`{lang}` and `{id}` are replaced in the URL) */
  @Prop() moreInfoLink: string = "https://zvv.ch/{lang}/fahrplan-und-informationen/verkehrslage.html?himIds={id}";

  /**
   * Date adapter, for custom parsing/formatting.
   * Must be object with a `parse` function which accepts a `string` and returns a `Date`,
   * and a `format` function which accepts a `Date` and returns a `string`.
   */
  @Prop() dateAdapter: StzhLocaleAdapter;

  /** Translation strings. */
  @Prop() localization: StzhVbzTickerLocalizedText;

  @Element() element: HTMLStzhVbzTickerElement;

  private globalDisturbances: VbzTickerDisturbance[] = [];
  private linesWithDisturbances: VbzTickerLine[] = [];
  private linesWithConstructions: VbzTickerLine[] = [];
  private collator: Intl.Collator;

  private counter = {
    [`${VBZ.DISTURBANCE_GLOBAL}`]: 0,
    [`${VBZ.DISTURBANCE_INFORMATION}`]: 0,
    [`${VBZ.DISTURBANCE_CONSTRUCTION}`]: 0,
    [`${VBZ.DISTURBANCE_NORMAL}`]: 0,
    [`${VBZ.DISTURBANCE_EVENT}`]: 0,
    [`${VBZ.DISTURBANCE_BUS}`]: 0,
  };

  @Watch("localization")
  localizationWatcher() {
    this.collator = new Intl.Collator(this.localization.$locale, {
      numeric: true,
    });
  }

  @Watch("api")
  async apiWatcher(newValue: string) {
    if (!newValue) {
      return;
    }

    let apiUrl = this.api.replace(/\{lang\}/, this.localization.$locale);
    apiUrl = apiUrl + (apiUrl.includes("?") ? "&" : "?") + Date.now();

    // load data from API
    const { data } = await axios.get(apiUrl);

    this.data = data;
  }

  @Watch("dateAdapter")
  dateAdapterWatcher(newValue: StzhLocaleAdapter) {
    if (!newValue) {
      this.dateAdapter = window.stzhComponents.utils.createFormatParseAdapter(
        this.localization.$formats,
        this.localization.$globals
      );
    }
  }

  @Watch("data")
  async dataWatcher(newValue: any) {
    if (typeof newValue === "string") {
      this._data = JSON.parse(newValue);
    } else {
      this._data = newValue;
    }

    const linesToDisplay: { [key: string]: VbzTickerLine } = {};

    this._data?.Message?.forEach(message => {
      const urls: VbzTickerInfoUrl[] = message.channel
        .filter(channel => channel.name === VBZ.CHANNEL_TICKER && channel.url)
        .map(channel => channel.url)
        .reduce((a, b) => a.concat(b), []);

      const infoUrl = urls.find(url => url.url.indexOf(VBZ.CHANNEL_INFO_URL_FILTER) !== -1);

      if (message.baseType === VBZ.BASE_TYPE_GLOBAL) {
        this.counter[VBZ.DISTURBANCE_GLOBAL]++;

        this.globalDisturbances.push({
          id: message.id,
          head: message.head,
          category: message.category,
          baseType: message.baseType,
          startDate: new Date(`${message.sDate}T${message.sTime}+02:00`),
          endDate: new Date(`${message.eDate}T${message.eTime}+02:00`),
          altStart: message.altStart,
          altEnd: message.altEnd,
          infoUrl: infoUrl,
        });
      } else {
        this.counter[message.category]++;
      }

      // don't add to linesWithDisturbances if no line/product has been linked to
      // or if it is a information disturbance or global
      if (
        message.affectedProduct &&
        message.category !== VBZ.DISTURBANCE_INFORMATION &&
        message.baseType !== VBZ.BASE_TYPE_GLOBAL
      ) {
        const affectedLines = [];

        message.affectedProduct.forEach(product => {
          if (typeof linesToDisplay[product.lineId] === "undefined") {
            linesToDisplay[product.lineId] = {
              line: product.line,
              name: product.name,
              category: product.cls,
              disturbances: [],
              alert: false,
            };
          }

          if (message.category === VBZ.DISTURBANCE_EVENT || message.category === VBZ.DISTURBANCE_GLOBAL) {
            linesToDisplay[product.lineId].alert = true;
          }

          // only add disturbance message to line if it was not already added (avoiding same message twice)
          if (!affectedLines.includes(product.lineId)) {
            affectedLines.push(product.lineId);

            linesToDisplay[product.lineId].disturbances.push({
              id: message.id,
              head: message.head,
              category: message.category,
              baseType: message.baseType,
              startDate: new Date(`${message.sDate}T${message.sTime}+02:00`),
              endDate: new Date(`${message.eDate}T${message.eTime}+02:00`),
              altStart: message.altStart,
              altEnd: message.altEnd,
              infoUrl: infoUrl,
            });
          }
        });
      }
    });

    this.linesWithDisturbances = Object.keys(linesToDisplay)
      .map(key => linesToDisplay[key])
      .filter(
        line => line.disturbances.findIndex(disturbance => disturbance.category !== VBZ.DISTURBANCE_CONSTRUCTION) > -1
      );

    this.linesWithConstructions = Object.keys(linesToDisplay)
      .map(key => linesToDisplay[key])
      .filter(
        line => line.disturbances.findIndex(disturbance => disturbance.category === VBZ.DISTURBANCE_CONSTRUCTION) > -1
      );

    // sort by line name
    this.linesWithDisturbances.sort((a, b) => this.collator.compare(a.line, b.line));
    this.linesWithConstructions.sort((a, b) => this.collator.compare(a.line, b.line));
  }

  async componentWillLoad() {
    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "vbz-ticker");
      this.localizationWatcher();
    }

    this.dateAdapterWatcher(this.dateAdapter);

    if (this.data) {
      await this.dataWatcher(this.data);
    } else {
      await this.apiWatcher(this.api);
    }
  }

  renderDisturbance(disturbance: VbzTickerDisturbance) {
    return (
      <div
        class={{
          "stzh-vbz-ticker__disturbance": true,
          [`stzh-vbz-ticker__disturbance--type${disturbance.category}`]: !!disturbance.category,
        }}
      >
        <div class="stzh-vbz-ticker__disturbance-text">
          <div class="stzh-vbz-ticker__disturbance-title">{disturbance.head}</div>

          <div class="stzh-vbz-ticker__disturbance-date">
            {this.dateAdapter.formatSpan(
              disturbance.altStart || disturbance.startDate,
              disturbance.altEnd || disturbance.endDate,
              "date"
            )}
          </div>

          {disturbance.infoUrl ? (
            <stzh-link class="stzh-vbz-ticker__disturbance-link" size="small" href={disturbance.infoUrl.url}>
              {this.localization.moreInfo}
            </stzh-link>
          ) : (
            <stzh-link
              class="stzh-vbz-ticker__disturbance-link"
              size="small"
              target="_blank"
              icon="external-link"
              href={this.moreInfoLink.replace(/\{lang\}/, this.localization.$locale).replace(/\{id\}/, disturbance.id)}
            >
              {this.localization.moreInfo}
            </stzh-link>
          )}
        </div>
      </div>
    );
  }

  render() {
    const classes = {
      "stzh-vbz-ticker": true,
    };

    return (
      <Host>
        <div class={classes}>
          <div class="stzh-vbz-ticker__content">
            {/* TODO: Old stuff currently in verification whether to redesign or remove. */}

            {/*{this.linesWithDisturbances.length === 0 && this.counter[VBZ.DISTURBANCE_GLOBAL] === 0 && (*/}
            {/*  <div class="stzh-vbz-ticker__title-part stzh-vbz-ticker__title-part--no-disturbances">*/}
            {/*    <stzh-icon class="stzh-vbz-ticker__icon" name="checkmark-circle"></stzh-icon>*/}
            {/*    {this.localization.noDisturbances}*/}
            {/*  </div>*/}
            {/*)}*/}

            {/*{this.counter[DISTURBANCE_GLOBAL] > 0 && (*/}
            {/*  <div class="stzh-vbz-ticker__title-part stzh-vbz-ticker__title-part--major-events">*/}
            {/*    <stzh-icon class="stzh-vbz-ticker__icon" name="important-warning"></stzh-icon>*/}
            {/*    {this.localization.majorEventTitle}*/}
            {/*  </div>*/}
            {/*)}*/}

            {/*<div class="stzh-vbz-ticker__description">*/}
            {/*  {this.counter[VBZ.DISTURBANCE_GLOBAL] > 0*/}
            {/*    ? this.localization.globalDisturbance*/}
            {/*    : this.localization.affectedLines}*/}
            {/*</div>*/}

            {/*{this.counter[VBZ.DISTURBANCE_GLOBAL] > 0 && (*/}
            {/*  <div class="stzh-vbz-ticker__chip" role="listitem">*/}
            {/*    <stzh-tooltip class="stzh-vbz-ticker__tooltip">*/}
            {/*      <stzh-vbz-linechip global></stzh-vbz-linechip>*/}
            {/*      <div class="stzh-vbz-ticker__tooltip-content" slot="content">*/}
            {/*        {this.globalDisturbances.map(disturbance => this.renderDisturbance(disturbance))}*/}
            {/*      </div>*/}
            {/*    </stzh-tooltip>*/}
            {/*  </div>*/}
            {/*)}*/}

            {this.counter[VBZ.DISTURBANCE_NORMAL] > 0 && (
              <Fragment>
                <div class="stzh-vbz-ticker__title">
                  <div class="stzh-vbz-ticker__title-part stzh-vbz-ticker__title-part--disturbances">
                    <stzh-icon class="stzh-vbz-ticker__icon" name="warning"></stzh-icon>
                    {this.localization.disturbancesTitle}
                  </div>
                </div>
                <div class="stzh-vbz-ticket__lines">
                  <div class="stzh-vbz-ticker__chips" role="list">
                    {this.linesWithDisturbances.map(line => (
                      <div class="stzh-vbz-ticker__chip" role="listitem">
                        <stzh-tooltip class="stzh-vbz-ticker__tooltip">
                          <stzh-vbz-linechip line={line.line} alert={line.alert}></stzh-vbz-linechip>
                          <div class="stzh-vbz-ticker__tooltip-content" slot="content">
                            {line.disturbances.map(disturbance => this.renderDisturbance(disturbance))}
                          </div>
                        </stzh-tooltip>
                      </div>
                    ))}
                  </div>
                </div>
              </Fragment>
            )}

            {/* Constructions etc. */}
            {(this.counter[VBZ.DISTURBANCE_CONSTRUCTION] > 0 ||
              this.counter[VBZ.DISTURBANCE_EVENT] > 0 ||
              this.counter[VBZ.DISTURBANCE_BUS] > 0) && (
              <Fragment>
                <div class="stzh-vbz-ticker__title">
                  <div class="stzh-vbz-ticker__title-parts">
                    <div class="stzh-vbz-ticker__title-part stzh-vbz-ticker__title-part--others">
                      <stzh-icon class="stzh-vbz-ticker__icon" name="construction"></stzh-icon>
                      {this.localization.constructionsTitle}
                    </div>
                  </div>
                </div>
                <div class="stzh-vbz-ticket__lines">
                  <div class="stzh-vbz-ticker__chips" role="list">
                    {this.linesWithConstructions.map(line => (
                      <div class="stzh-vbz-ticker__chip" role="listitem">
                        <stzh-tooltip class="stzh-vbz-ticker__tooltip">
                          <stzh-vbz-linechip line={line.line} alert={line.alert}></stzh-vbz-linechip>
                          <div class="stzh-vbz-ticker__tooltip-content" slot="content">
                            {line.disturbances.map(disturbance => this.renderDisturbance(disturbance))}
                          </div>
                        </stzh-tooltip>
                      </div>
                    ))}
                  </div>
                </div>
              </Fragment>
            )}

            <div class="stzh-vbz-ticker__link">
              <slot name="link"></slot>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
